import { AnimatePresence, motion } from "motion/react";
import PropTypes, { bool, func } from "prop-types";

import styled from "styled-components/macro";

import FnrButtonWrapper from "UI/FnrButtonWrapper";
import FnrContainedIcon from "UI/FnrContainedIcon";
import FnrTypography from "UI/FnrTypography";
import { colors } from "UI/colors";
import { inOutMotion } from "UI/motionStyles";

import { useEntranceSettled } from "../hooks/useEntranceSettled";

/**
 * Calm, non-blocking "we couldn't verify this address" callout — informational only
 * (it never gates submission), so it wears a neutral-gray pill, not a yellow warning.
 * This is the SOLO form: when the address is BOTH unverifiable and standardized
 * (base-retain), surfaces render the merged `CombinedCallout` instead. Two signals
 * drive it (the consumer picks): the hook's `liveUnverifiable` while the user is in the
 * fields, and the committed unverifiable verdict at save.
 *
 * Does no positioning of its own — the surface places it.
 */
export default function UnverifiableCallout({
  open,
  flush,
  bare,
  align,
  iconSide,
  minHeight,
  onDismiss,
}) {
  const { interactive, setAnimating } = useEntranceSettled();
  // No `noPadding`: the padded container's fixed 32px box normalizes the
  // glyph's width so the text start sits at a consistent offset.
  const icon = (
    <FnrContainedIcon
      icon="map-marker-question fas"
      size="sm"
      noBackgroundColor
      color="textSecondary"
    />
  );

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          {...inOutMotion}
          exit={inOutMotion.exit}
          onAnimationStart={() => setAnimating(true)}
          onAnimationComplete={() => setAnimating(false)}
        >
          <Wrapper
            role="region"
            aria-label="address-unverifiable"
            $flush={flush}
            $bare={bare}
            $align={align}
            $iconRight={iconSide === "right"}
            $minHeight={minHeight}
            $hasTrailing={Boolean(onDismiss)}
            $interactive={interactive}
          >
            {iconSide !== "right" && icon}
            <Text>
              <FnrTypography
                type="copy"
                fontSize="sm"
                color="textSecondaryDark"
                lineHeight={1.2}
              >
                We couldn&apos;t verify this address
              </FnrTypography>
              <FnrTypography
                type="copy"
                fontSize="xs"
                color="textSecondaryLight"
                lineHeight={1.2}
              >
                If you know it&apos;s correct, you&apos;re all set.
              </FnrTypography>
            </Text>
            {iconSide === "right" && icon}
            {onDismiss && (
              <CloseX
                type="button"
                onClick={onDismiss}
                aria-label="dismiss unverifiable notice"
              >
                <FnrContainedIcon
                  icon="times-circle fas"
                  size="sm"
                  noBackgroundColor
                  noPadding
                  color="currentColor"
                />
              </CloseX>
            )}
          </Wrapper>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

UnverifiableCallout.propTypes = {
  open: bool,
  // Drops the inline top/bottom margin so the callout sits flush as standalone content (e.g. in a popover).
  flush: bool,
  // Strips the pill chrome (background + border) so content floats on a parent that already supplies one.
  bare: bool,
  // Anything other than "left" (incl. unset) centers; inline surfaces left-align, the boards popover centers.
  align: PropTypes.oneOf(["left", "center"]),
  // Trailing icon instead of leading — the boards popover's lone unverifiable slide puts the marker on the right.
  iconSide: PropTypes.oneOf(["left", "right"]),
  // Override min-height (px) to force a corner radius (min-height/2) matching the container (e.g. 48 → 24px corner).
  minHeight: PropTypes.number,
  // Opt-in dismiss X (admin profile passes it); surfaces that omit it render no X.
  onDismiss: func,
};

// Gray-pill geometry, sibling of StandardizationNotice (blue). NEST_GAP is the base inner
// padding and the gap a right-side marker nests at. The trailing dismiss circle uses its own
// right padding (below) so its always-visible glyph-circle reads concentric in the end-cap at
// this callout's two-line height (equal 6px to top, bottom, and the edge for the 28px circle).
const NEST_GAP = 4;
const BUTTON_SIZE = 28;

const Wrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
  max-width: 100%;
  margin-top: ${({ $flush }) => ($flush ? "0px" : "8px")};
  margin-bottom: ${({ $flush }) => ($flush ? "0px" : "8px")};
  margin-left: ${({ $align }) => ($align === "left" ? "0" : "auto")};
  margin-right: auto;
  /* Bare text against the pill's end-cap curve needs 12px to clear it; the boards right-side
     marker nests at NEST_GAP, and the trailing dismiss circle uses 5px so its gap to the edge
     matches its 6px top/bottom (concentric). Leading side: 4px before the icon box, or 8px of
     bare text on the boards icon-right slide to clear the popover's corner-close chip. */
  padding: ${NEST_GAP}px;
  padding-left: ${({ $iconRight }) => ($iconRight ? 8 : 4)}px;
  padding-right: ${({ $iconRight, $hasTrailing }) =>
    $hasTrailing ? 5 : $iconRight ? NEST_GAP : 12}px;
  /* Floor when a trailing circle button renders: BUTTON_SIZE + 2*NEST_GAP + 2px border
     (1px top + 1px bottom). Callers may override via $minHeight (the "both" slide passes 48
     so the 999px radius resolves to a 24px corner matching the popover shell). */
  min-height: ${({ $minHeight, $hasTrailing }) =>
    $minHeight != null
      ? `${$minHeight}px`
      : $hasTrailing
      ? `${BUTTON_SIZE + NEST_GAP * 2 + 2}px`
      : "auto"};
  border: 1px solid ${({ $bare }) => ($bare ? "transparent" : colors.fnGray200)};
  border-radius: 999px;
  background: ${({ $bare }) => ($bare ? "transparent" : colors.fnGray100)};
  transition: background 200ms, border-color 200ms;
  /* Inert while sliding in so the dismiss button can't flick :hover as the pill
     moves through the cursor — see useEntranceSettled. */
  pointer-events: ${({ $interactive }) => ($interactive ? "auto" : "none")};
`;

// Flex column blockifies the typography element so its own lineHeight governs
// the line boxes (a bare inline would ride this div's 16px strut instead).
const Text = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

// The times-circle glyph IS the circle, so the button carries no chrome of its own —
// no background fill, just the 24px glyph filling the box. Hover darkens the glyph via
// currentColor; the 300ms matches FnrContainedIcon's internal color transition.
const CloseX = styled(FnrButtonWrapper)`
  flex-shrink: 0;
  /* Beyond the 4px flex gap so the text↔circle space matches the 8px gap to the
     pill's right edge (the circle reads evenly spaced on both sides). */
  margin-left: 3px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${BUTTON_SIZE}px;
  height: ${BUTTON_SIZE}px;
  border: none;
  background: transparent;
  color: ${colors.fnGray600};
  transition: color 300ms ease, transform 100ms;
  /* The FA times-circle glyph's ink sits ~3px high within its line box at this
     font size, so the visible circle isn't centered by box-centering alone — a
     measured (screenshot, not getBoundingClientRect) translateY recenters it. */
  & .fa-times-circle {
    font-size: ${BUTTON_SIZE}px;
    transform: translateY(3px);
  }
  &:hover,
  &:focus-visible {
    color: ${colors.fnGray800};
  }
  &:active {
    transform: scale(0.92);
  }
`;
